/**
 * Grafana Faro PII Redaction
 *
 * Mirrors the field-key–driven masking model already used for LogRocket
 * (see @mgmresorts/client-log-rocket's sanitizers/variations.js) so that
 * telemetry sent to Faro carries the same privacy guarantees. Masking is
 * key-name driven, not content/regex scanning, except for the URL email
 * check below which mirrors LogRocket's own `maskEmailInUrl` behavior.
 */

import type { APIEvent, BeforeSendHook, TransportItem } from '@grafana/faro-web-sdk';

type Masker = (value: unknown) => unknown;

const EMAIL_REGEX = /[^\s@/?#&=]+@[^\s@/?#&=]+\.[^\s@/?#&=]+/g;

const maskStr: Masker = value => (typeof value === 'string' ? '*'.repeat(value.length) : '[REDACTED]');

const maskFirstChar: Masker = value =>
  typeof value === 'string' && value.length > 0 ? `${value[0]}${'*'.repeat(value.length - 1)}` : '[REDACTED]';

const maskEmail: Masker = value => {
  if (typeof value !== 'string') {
    return '[REDACTED]';
  }
  const [local, domain] = value.split('@');
  if (!domain) {
    return maskStr(value);
  }
  return `${maskFirstChar(local)}@${maskStr(domain)}`;
};

// Name fields
const nameFieldVariations: Record<string, Masker> = {
  cardHolderName: maskStr,
  family_name: maskStr,
  firstName: maskStr,
  first_name: maskStr,
  fullName: maskStr,
  full_name: maskStr,
  given_name: maskStr,
  lastName: maskStr,
  last_name: maskStr,
  nameOnCard: maskStr,
};

// MGM / mlife id fields
const mgmIdVariations: Record<string, Masker> = {
  'com.mgm.gse.id': maskStr,
  'com.mgm.mlife_number': maskStr,
  mgmId: maskStr,
  mlife: maskStr,
  mlifeAccountNumber: maskStr,
  mlifeNumber: maskStr,
};

// Birthday fields
const birthdayFieldVariations: Record<string, Masker> = {
  age: maskStr,
  birthdate: maskStr,
  birthday: maskStr,
  dateOfBirth: maskStr,
};

// Address fields (LogRocket's list, extended with this app's own data-private fields)
const addressFieldVariations: Record<string, Masker> = {
  postalCode: maskStr,
  profilePostalCode: maskStr,
  profileStreet1: maskStr,
  profileStreet2: maskStr,
  street1: maskStr,
  street2: maskStr,
  address1: maskStr,
  address2: maskStr,
  city: maskStr,
  state: maskStr,
  zipCode: maskStr,
  countryCode: maskStr,
};

// Email fields
const emailFieldVariations: Record<string, Masker> = {
  email: maskEmail,
  emailAddress: maskEmail,
  emailAddress1: maskEmail,
  emailAddress2: maskEmail,
  email_address: maskEmail,
  login: maskEmail,
  sub: maskEmail,
};

// Phone fields
const phoneFieldVariations: Record<string, Masker> = {
  mobile_phone: maskStr,
  number: maskStr,
  phone: maskStr,
  phoneNumber: maskStr,
  phone_number: maskStr,
  telephone: maskStr,
};

// Auth fields
// NOTE: LogRocket's own denylist also masks a generic `value` key (for Redux
// action/state shapes like { key: 'password', value }). That collides with
// Faro's own Exception payload schema, where `payload.value` holds the actual
// error message, so `value` is intentionally omitted from full-field masking
// here to avoid destroying every captured error message. It is still scanned
// for embedded emails via TEXT_SCAN_KEYS below.
const authFieldVariations: Record<string, Masker> = {
  accessToken: maskFirstChar,
  access_token: maskFirstChar,
  cardCVV: maskStr,
  id_token: maskFirstChar,
  password: maskStr,
};

// Fields specific to this app's forms, marked data-private in the DOM
// (Profile.tsx, LicensePlate.tsx, Passkey components) but not covered above.
const appSpecificVariations: Record<string, Masker> = {
  plateNumber: maskStr,
  plateState: maskStr,
  credentialId: maskFirstChar,
  passkeyId: maskFirstChar,
  privateKey: maskStr,
};

export const PII_FIELD_MASKERS: Record<string, Masker> = {
  ...mgmIdVariations,
  ...nameFieldVariations,
  ...birthdayFieldVariations,
  ...addressFieldVariations,
  ...emailFieldVariations,
  ...phoneFieldVariations,
  ...authFieldVariations,
  ...appSpecificVariations,
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Masks any email address found within a URL (path segment or query string),
 * mirroring LogRocket's `maskEmailInUrl`.
 */
export function maskEmailInUrl(url: string): string {
  if (typeof url !== 'string') {
    return url;
  }
  return url.replace(EMAIL_REGEX, match => maskEmail(match) as string);
}

// Keys holding free-form text (URLs, error messages) that aren't masked
// wholesale by `transforms` but are still scanned for embedded emails, e.g.
// Faro's Exception payload `value` (the raw error message).
const TEXT_SCAN_KEYS = new Set(['url', 'href', 'page', 'http.url', 'url.full', 'value']);

/**
 * Recursively walks an object/array once, masking any key present in
 * `transforms` outright and scanning free-text `TEXT_SCAN_KEYS` fields for
 * embedded emails, leaving all other keys and structure untouched. Mirrors
 * LogRocket's `maskObject(transforms)(obj)`.
 */
export function maskPiiFields<T>(value: T, transforms: Record<string, Masker> = PII_FIELD_MASKERS): T {
  if (Array.isArray(value)) {
    return value.map(item => maskPiiFields(item, transforms)) as unknown as T;
  }

  if (isPlainObject(value)) {
    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
      if (Object.prototype.hasOwnProperty.call(transforms, key)) {
        result[key] = isPlainObject(val) || Array.isArray(val) ? maskPiiFields(val, transforms) : transforms[key](val);
      } else if (typeof val === 'string' && TEXT_SCAN_KEYS.has(key)) {
        result[key] = maskEmailInUrl(val);
      } else {
        result[key] = maskPiiFields(val, transforms);
      }
    }
    return result as unknown as T;
  }

  return value;
}

/**
 * Builds the `beforeSend` hook passed to `initializeFaro`. Faro invokes this
 * hook for every outgoing signal (log, exception, event, measurement, trace)
 * before it reaches a transport, making it the single equivalent of
 * LogRocket's requestSanitizer/responseSanitizer/actionSanitizer combined.
 */
export function createFaroBeforeSend(): BeforeSendHook<APIEvent> {
  return (item: TransportItem<APIEvent>) => {
    if (!isPlainObject(item)) {
      return item;
    }

    return maskPiiFields(item);
  };
}
